<script setup>
import EmailingExclusionTable from '@frontJs/components/tables/projectManagement/modules/EmailingExclusionTable.vue';
import EmailingVariableTable from '@frontJs/components/tables/projectManagement/modules/EmailingVariableTable.vue';
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import DisplayTemplateHelperAction from '@js/components/actions/DisplayTemplateHelperAction.vue';
import DisplayVariablesAction from '@js/components/actions/DisplayVariablesAction.vue';
import ManageMediasAction from '@js/components/actions/ManageMediasAction.vue';
import AlertBox from '@js/components/alerts/AlertBox.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import HtmlPreview from '@js/components/contents/HtmlPreview.vue';
import CodeField from '@js/components/fields/CodeField.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormSeparator from '@js/components/forms/FormSeparator.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { object, string } from 'yup';

const {
    tenant,
    emailing,
    emailingVariableInformation,
    emailingContactDataTypes,
    emailingExclusionDataTypes,
    semSegmentations,
} = defineProps({
    tenant: {
        required: true,
        type: Object,
    },
    emailing: {
        required: false,
        type: Object,
    },
    emailingVariableInformation: {
        type: Object,
        required: false,
    },
    emailingContactDataTypes: {
        type: Array,
        required: true,
    },
    emailingExclusionDataTypes: {
        type: Array,
        required: true,
    },
    semSegmentations: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const loading = ref(true);

const loadingPreview = ref(true);

const eCommerceStores = ref([]);

const eCommerceCustomerGroups = ref([]);

const emailingConfiguration = ref(null);

const tenants = ref([]);

const exclusionSemSegmentations = ref([]);

const previewHtml = ref('');

const selectedTab = ref();

const isUnsubscriptionUrlMissing = computed(
    () => !form.template.includes('unsubscription_url'),
);

const hasECommerceStores = computed(() => eCommerceStores.value.length > 0);

const form = reactive({
    eCommerceStore: null,
    name: emailing ? emailing.name : '',
    description: emailing ? emailing.description : '',
    date: emailing ? emailing.date : null,
    isActive: emailing ? emailing.isActive : false,
    senderName: emailing ? emailing.senderName : '',
    senderEmail: emailing ? emailing.senderEmail : '',
    replyToName: emailing ? emailing.replyToName : '',
    replyToEmail: emailing ? emailing.replyToEmail : '',
    subject: emailing ? emailing.subject : '',
    template: emailing ? emailing.template : '',
    contactDataType: emailing
        ? emailingContactDataTypes.find(
              (type) => emailing.contactDataType.value === type.value,
          )
        : emailingContactDataTypes[0],
    contactData:
        emailing && emailing.contactDataType.value === 'list'
            ? {
                  emails: emailing.contactData.emails
                      ? emailing.contactData.emails.join('\n')
                      : '',
              }
            : emailing && emailing.contactDataType.value === 'segmentation'
              ? {
                    semSegmentation: emailing.contactData.semSegmentationId
                        ? semSegmentations.find(
                              (semSegmentation) =>
                                  semSegmentation.id ===
                                  emailing.contactData.semSegmentationId,
                          )
                        : null,
                }
              : {},
    exclusionDataType:
        emailing && emailing.exclusionDataType
            ? emailingExclusionDataTypes.find(
                  (type) => emailing.exclusionDataType.value === type.value,
              )
            : emailingExclusionDataTypes[0],
    exclusionData:
        emailing &&
        emailing.exclusionDataType &&
        emailing.exclusionDataType.value === 'list'
            ? null
            : emailing &&
                emailing.exclusionDataType &&
                emailing.exclusionDataType.value === 'segmentation'
              ? {
                    tenant: null,
                    semSegmentation: null,
                }
              : {},
});

const validationSchema = object({
    cmsStore: object().nullable(),
    name: string().required('Le nom est requis'),
    description: string().nullable(),
    date: string().nullable(),
    isActive: string().required('Le statut actif est requis'),
    senderName: string().required("Le nom de l'expéditeur est requis"),
    senderEmail: string()
        .required("L'email de l'expéditeur est requis")
        .email("L'email de l'expéditeur doit être une adresse email valide"),
    replyToName: string().nullable(),
    replyToEmail: string()
        .nullable()
        .email("L'email de la réponse doit être une adresse email valide"),
    subject: string().required('Le sujet est requis'),
    template: string().required('Le template est requis'),
    contactDataType: object().required(
        'Le type de données de contact est requis',
    ),
    contactData: object().when('contactDataType', {
        is: (type) => type && type.value === 'list',
        then: (schema) =>
            schema.shape({
                emails: string().required('Les emails sont requis'),
            }),
        otherwise: (schema) =>
            schema.when('contactDataType', {
                is: (type) => type && type.value === 'segmentation',
                then: (schema) =>
                    schema.shape({
                        semSegmentation: object().nullable(),
                    }),
                otherwise: (schema) => schema.nullable(),
            }),
    }),
    exclusionDataType: object().required(
        "Le type de données d'exclusion est requis",
    ),
    exclusionData: object().when('exclusionDataType', {
        is: (type) => type && type.value === 'list',
        then: (schema) => schema.nullable(),
        otherwise: (schema) =>
            schema.when('exclusionDataType', {
                is: (type) => type && type.value === 'segmentation',
                then: (schema) =>
                    schema.shape({
                        tenant: object()
                            .nullable()
                            .required('Le client est requis'),
                        semSegmentation: object()
                            .nullable()
                            .required('La segmentation est requise'),
                    }),
                otherwise: (schema) => schema.nullable(),
            }),
    }),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.modules.postEmailing', {
                tenant: tenant.id,
                ...(emailing
                    ? {
                          emailing: emailing.id,
                      }
                    : {}),
            }),
            {
                cmsStoreId: hasECommerceStores.value
                    ? (form.eCommerceStore?.cmsStoreId ?? null)
                    : (emailing?.cmsStoreId ?? null),
                name: form.name,
                description: form.description,
                isActive: form.isActive,
                date: form.date,
                senderName: form.senderName,
                senderEmail: form.senderEmail,
                replyToName: form.replyToName,
                replyToEmail: form.replyToEmail,
                subject: form.subject,
                template: form.template,
                contactDataType: form.contactDataType.value,
                contactData: {
                    ...(form.contactDataType.value === 'list'
                        ? {
                              emails: form.contactData.emails
                                  .split('\n')
                                  .map((email) => email.trim())
                                  .filter((email) => email !== ''),
                          }
                        : form.contactDataType.value === 'segmentation'
                          ? {
                                semSegmentationId:
                                    form.contactData.semSegmentation.id,
                            }
                          : {}),
                },
                exclusionDataType: form.exclusionDataType.value,
                exclusionData:
                    form.exclusionDataType.value === 'list'
                        ? null
                        : form.exclusionDataType.value === 'segmentation'
                          ? {
                                tenantId: form.exclusionData.tenant?.id ?? null,
                                semSegmentationId:
                                    form.exclusionData.semSegmentation?.id ??
                                    null,
                            }
                          : {},
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.modules.emailings', {
                    tenant: tenant.id,
                }),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.modules.emailings', {
                    tenant: tenant.id,
                }),
            );
        });
}

function preview() {
    loadingPreview.value = true;

    axios
        .post(
            route(
                'front.projectManagement.modules.postPreviewEmailingTemplate',
                {
                    tenant: tenant.id,
                    ...(emailing
                        ? {
                              emailingId: emailing.id,
                          }
                        : {}),
                },
            ),
            {
                template: form.template,
            },
        )
        .then((response) => {
            previewHtml.value = response.data.html;
        })
        .finally(() => {
            loadingPreview.value = false;
        })
        .catch(() => {
            previewHtml.value =
                "Une erreur est survenue lors de la génération de l'aperçu.";
        });
}

function updateData() {
    loading.value = true;

    Promise.all([
        axios.get(route('front.eCommerce.getStores', { tenant: tenant.id })),
        axios.get(
            route('front.eCommerce.getCustomerGroups', { tenant: tenant.id }),
        ),
        axios.get(
            route('front.projectManagement.modules.getEmailingConfiguration', {
                tenant: tenant.id,
            }),
        ),
        axios.get(route('front.getTenants')),
    ])
        .then(function (response) {
            eCommerceStores.value = response[0].data;

            if (emailing) {
                form.eCommerceStore = eCommerceStores.value.find(
                    (eCommerceStore) =>
                        eCommerceStore.cmsStoreId === emailing.cmsStoreId,
                );
            } else {
                form.eCommerceStore = eCommerceStores.value[0] ?? null;
            }

            eCommerceCustomerGroups.value = response[1].data;

            if (emailing && emailing.contactDataType.value === 'segmentation') {
                form.contactData.eCommerceCustomerGroup =
                    eCommerceCustomerGroups.value.find(
                        (group) =>
                            group.cmsCustomerGroupId ===
                            emailing.contactData.cmsCustomerGroupId,
                    );
            }

            emailingConfiguration.value = response[2].data;

            if (!emailing) {
                if (emailingConfiguration.value.data.defaultSenderName) {
                    form.senderName =
                        emailingConfiguration.value.data.defaultSenderName;
                }

                if (emailingConfiguration.value.data.defaultSenderEmail) {
                    form.senderEmail =
                        emailingConfiguration.value.data.defaultSenderEmail;
                }

                if (emailingConfiguration.value.data.defaultReplyToName) {
                    form.replyToName =
                        emailingConfiguration.value.data.defaultReplyToName;
                }

                if (emailingConfiguration.value.data.defaultReplyToEmail) {
                    form.replyToEmail =
                        emailingConfiguration.value.data.defaultReplyToEmail;
                }
            }

            tenants.value = response[3].data;

            if (
                emailing &&
                emailing.exclusionDataType &&
                emailing.exclusionDataType.value === 'segmentation' &&
                emailing.exclusionData?.tenantId
            ) {
                form.exclusionData.tenant =
                    tenants.value.find(
                        (t) => t.id === emailing.exclusionData.tenantId,
                    ) ?? null;
            }
        })
        .finally(() => {
            loading.value = false;
        })
        .catch((error) => {
            toastError(error);
        });
}

watch(
    () => form.exclusionDataType,
    (newType) => {
        if (newType?.value === 'segmentation') {
            form.exclusionData = {
                tenant: null,
                semSegmentation: null,
            };
            exclusionSemSegmentations.value = [];
        } else if (newType?.value === 'list') {
            form.exclusionData = null;
        } else {
            form.exclusionData = {};
        }
    },
);

watch(
    () => form.exclusionData?.tenant,
    (newTenant) => {
        if (form.exclusionDataType?.value !== 'segmentation') {
            return;
        }

        exclusionSemSegmentations.value = [];
        form.exclusionData.semSegmentation = null;

        if (newTenant) {
            axios
                .get(
                    route('front.projectManagement.sem.getSegmentations', {
                        tenant: newTenant.id,
                    }),
                )
                .then((response) => {
                    exclusionSemSegmentations.value = response.data;

                    if (emailing?.exclusionData?.semSegmentationId) {
                        form.exclusionData.semSegmentation =
                            exclusionSemSegmentations.value.find(
                                (s) =>
                                    s.id ===
                                    emailing.exclusionData.semSegmentationId,
                            ) ?? null;
                    }
                });
        }
    },
);

onMounted(() => {
    updateData();
    preview();
});
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
        :loading="loading"
    >
        <TabLayout
            v-model:selected-tab="selectedTab"
            :tabs="{
                information: {
                    title: 'Informations',
                    contentSlotName: 'information',
                    forceMount: true,
                },
                email: {
                    title: 'Email',
                    contentSlotName: 'email',
                    forceMount: true,
                },
                contact: {
                    title: 'Contact',
                    contentSlotName: 'contact',
                    forceMount: true,
                },
                emailingVariables: {
                    title: 'Variables',
                    contentSlotName: 'emailingVariables',
                    forceMount: true,
                },
                emailExclusions: {
                    title: 'Exclusions d\'emails',
                    contentSlotName: 'emailExclusions',
                    forceMount: true,
                },
            }"
        >
            <template #information>
                <FormContent>
                    <template v-if="hasECommerceStores">
                        <ListboxField
                            v-model="form.eCommerceStore"
                            field-id="cmsStore"
                            field-name="cmsStore"
                            label="Magasin"
                            :options="eCommerceStores"
                            has-empty-option
                        >
                            <template #buttonContent>
                                <template v-if="form.eCommerceStore">
                                    {{ form.eCommerceStore.name }}
                                </template>

                                <template v-else> &nbsp; </template>
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.name }}
                            </template>
                        </ListboxField>

                        <FormSeparator />
                    </template>

                    <InputField
                        v-model="form.name"
                        label="Nom"
                        field-name="name"
                        field-id="name"
                        required
                    />

                    <EditorField
                        v-model="form.description"
                        label="Description"
                        field-name="description"
                        field-id="description"
                        size="small"
                    />

                    <FormSeparator />

                    <SwitchField
                        v-model="form.isActive"
                        label="Est actif"
                        field-name="isActive"
                        field-id="isActive"
                    />

                    <InputField
                        v-model="form.date"
                        label="Date d'envoi"
                        field-name="date"
                        field-id="date"
                        type="datetime-local"
                        :min="new Date().toISOString().slice(0, 16)"
                    />

                    <FormSeparator />

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.senderName"
                            label="Nom de l'expéditeur"
                            field-name="senderName"
                            field-id="senderName"
                            required
                        />

                        <InputField
                            v-model="form.senderEmail"
                            label="Email de l'expéditeur"
                            field-name="senderEmail"
                            field-id="senderEmail"
                            type="email"
                            required
                        />

                        <InputField
                            v-model="form.replyToName"
                            label="Nom de la réponse"
                            field-name="replyToName"
                            field-id="replyToName"
                        />

                        <InputField
                            v-model="form.replyToEmail"
                            label="Email de la réponse"
                            field-name="replyToEmail"
                            field-id="replyToEmail"
                            type="email"
                        />
                    </FormGrid>
                </FormContent>
            </template>

            <template #email>
                <FormColumn>
                    <AlertBox v-if="isUnsubscriptionUrlMissing" type="warning">
                        La variable
                        <code v-pre>{{ $unsubscription_url }}</code> est absente
                        du template. Le lien de désinscription ne sera pas
                        affiché dans l'email.
                    </AlertBox>

                    <FormContent>
                        <InputField
                            v-model="form.subject"
                            label="Sujet"
                            field-name="subject"
                            field-id="subject"
                            required
                        />

                        <FormGrid :columns="2">
                            <CodeField
                                v-model="form.template"
                                label="Template"
                                field-name="template"
                                field-id="template"
                                :disabled="loadingPreview"
                                required
                            />

                            <HtmlPreview
                                :html="previewHtml"
                                :loading="loadingPreview"
                            >
                                <template #actions>
                                    <DisplayTemplateHelperAction>
                                        <template #trigger>
                                            <ActionButton
                                                type="secondary"
                                                size="small"
                                            >
                                                Aide
                                            </ActionButton>
                                        </template>
                                    </DisplayTemplateHelperAction>

                                    <DisplayVariablesAction
                                        title="Variables"
                                        :variables="emailingVariableInformation"
                                    >
                                        <template #trigger>
                                            <ActionButton
                                                type="secondary"
                                                size="small"
                                            >
                                                Variables
                                            </ActionButton>
                                        </template>
                                    </DisplayVariablesAction>

                                    <ManageMediasAction
                                        :path="
                                            'tenant/' +
                                            tenant.id +
                                            '/emailings/media'
                                        "
                                    >
                                        <template #trigger>
                                            <ActionButton
                                                type="secondary"
                                                size="small"
                                            >
                                                Médias
                                            </ActionButton>
                                        </template>
                                    </ManageMediasAction>

                                    <ActionButton
                                        :callback="() => preview()"
                                        size="small"
                                        :loading="loadingPreview"
                                    >
                                        Prévisualiser
                                    </ActionButton>
                                </template>
                            </HtmlPreview>
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>

            <template #contact>
                <FormColumn>
                    <FormContent>
                        <ListboxField
                            v-model="form.contactDataType"
                            field-id="contactDataType"
                            field-name="contactDataType"
                            label="Type de données de contact"
                            :options="emailingContactDataTypes"
                            by="value"
                            required
                        >
                            <template #buttonContent>
                                {{ form.contactDataType.label }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.label }}
                            </template>
                        </ListboxField>
                    </FormContent>

                    <FormContent v-if="form.contactDataType.value === 'list'">
                        <TextareaField
                            v-model="form.contactData.emails"
                            label="Emails"
                            field-name="contactData.emails"
                            field-id="contactData.emails"
                            :legend="
                                'Un email par ligne. ' +
                                (form.contactData.emails
                                    ? form.contactData.emails.split('\n').length
                                    : 0) +
                                ' email(s) au total.'
                            "
                            rows="10"
                            required
                        />
                    </FormContent>

                    <FormContent
                        v-if="form.contactDataType.value === 'segmentation'"
                    >
                        <ListboxField
                            v-model="form.contactData.semSegmentation"
                            field-id="contactData.semSegmentation"
                            field-name="contactData.semSegmentation"
                            label="Segmentation"
                            :options="semSegmentations"
                            by="id"
                        >
                            <template #buttonContent>
                                {{ form.contactData.semSegmentation.name }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.name }}
                            </template>
                        </ListboxField>
                    </FormContent>
                </FormColumn>
            </template>

            <template #emailingVariables>
                <EmailingVariableTable
                    v-if="emailing"
                    :tenant="tenant"
                    :emailing="emailing"
                />

                <FormContent v-if="!emailing">
                    Les variables d'emailing sont disponibles uniquement après
                    la création de l'emailing.
                </FormContent>
            </template>

            <template #emailExclusions>
                <FormColumn>
                    <FormContent>
                        <ListboxField
                            v-model="form.exclusionDataType"
                            field-id="exclusionDataType"
                            field-name="exclusionDataType"
                            label="Type de données d'exclusion"
                            :options="emailingExclusionDataTypes"
                            by="value"
                            required
                        >
                            <template #buttonContent>
                                {{ form.exclusionDataType.label }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.label }}
                            </template>
                        </ListboxField>
                    </FormContent>

                    <EmailingExclusionTable
                        v-if="
                            emailing && form.exclusionDataType.value === 'list'
                        "
                        :tenant="tenant"
                        :emailing="emailing"
                    />

                    <FormContent
                        v-if="
                            !emailing && form.exclusionDataType.value === 'list'
                        "
                    >
                        La liste d'exclusion d'emails est disponible uniquement
                        après la création de l'emailing.
                    </FormContent>

                    <FormContent
                        v-if="form.exclusionDataType.value === 'segmentation'"
                    >
                        <FormGrid :columns="2">
                            <ComboboxField
                                v-model="form.exclusionData.tenant"
                                :options="tenants"
                                label="Client"
                                field-name="exclusionData.tenant"
                                field-id="exclusionData.tenant"
                                filteredOptionKey="name"
                                required
                            >
                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ComboboxField>

                            <ListboxField
                                v-model="form.exclusionData.semSegmentation"
                                field-id="exclusionData.semSegmentation"
                                field-name="exclusionData.semSegmentation"
                                label="Segmentation"
                                :options="exclusionSemSegmentations"
                                :disabled="!form.exclusionData.tenant"
                                by="id"
                                required
                            >
                                <template #buttonContent>
                                    <template
                                        v-if="
                                            form.exclusionData.semSegmentation
                                        "
                                    >
                                        {{
                                            form.exclusionData.semSegmentation
                                                .name
                                        }}
                                    </template>

                                    <template v-else> &nbsp; </template>
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="emailing"
                    :url="
                        route(
                            'front.projectManagement.modules.deleteEmailing',
                            {
                                tenant: tenant.id,
                                emailing: emailing.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.modules.emailings',
                                    {
                                        tenant: tenant.id,
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
